import * as THREE from 'three';

/**
 * The suggested-shot arrow drawn over the real table during a tutorial mission —
 * a dashed line that flows toward an arrowhead, following the mission's `hint`
 * polyline (see `resolveHint`). Purely illustrative: it never affects aim, and
 * sits alongside `AimVisuals` (the player's own live aim line) rather than
 * replacing it.
 */
export class TutorialHint {
  readonly group = new THREE.Group();
  private line: THREE.Line;
  private readonly arrow: THREE.Mesh;
  private on = false;
  private hasPath = false;
  /** Line-distance values from `computeLineDistances`, before the scrolling offset below. */
  private baseDistances: Float32Array | null = null;
  private flowOffset = 0;

  constructor() {
    const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3()]);
    const mat = new THREE.LineDashedMaterial({
      color: 0xffcc66,
      transparent: true,
      opacity: 0.85,
      dashSize: 0.045,
      gapSize: 0.032,
    });
    this.line = new THREE.Line(geo, mat);
    this.arrow = new THREE.Mesh(
      new THREE.ConeGeometry(0.018, 0.05, 12),
      new THREE.MeshBasicMaterial({ color: 0xffcc66, transparent: true, opacity: 0.85 })
    );
    this.group.add(this.line, this.arrow);
    this.group.visible = false;
  }

  setVisible(v: boolean): void {
    this.on = v;
    this.group.visible = this.on && this.hasPath;
  }

  /** Lay the hint along `points` (table metres); `ballRadius` sets its height off the felt. */
  setPath(points: readonly { x: number; y: number }[] | null, ballRadius: number): void {
    this.hasPath = !!points && points.length >= 2;
    this.group.visible = this.on && this.hasPath;
    if (!points || !this.hasPath) return;

    const z = ballRadius * 0.55;
    const pts3 = points.map((p) => new THREE.Vector3(p.x, p.y, z));
    this.line.geometry.dispose();
    this.line.geometry = new THREE.BufferGeometry().setFromPoints(pts3);
    this.line.computeLineDistances();
    const distAttr = this.line.geometry.getAttribute('lineDistance') as THREE.BufferAttribute;
    this.baseDistances = Float32Array.from(distAttr.array as ArrayLike<number>);
    this.flowOffset = 0;

    const a = pts3[pts3.length - 2];
    const b = pts3[pts3.length - 1];
    const dir = b.clone().sub(a);
    if (dir.lengthSq() > 1e-8) dir.normalize();
    else dir.set(0, 1, 0);
    this.arrow.position.copy(b);
    this.arrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  }

  /**
   * Scroll the dashes so the line reads as flowing toward the target. Three's
   * LineDashedMaterial has no dashOffset uniform in this version, so the scroll
   * is done by hand: shift every vertex's cumulative line-distance and let the
   * dash pattern (a function of that distance) fall where it may.
   */
  tick(dt: number): void {
    if (!this.group.visible || !this.baseDistances) return;
    this.flowOffset -= dt * 0.35;
    const attr = this.line.geometry.getAttribute('lineDistance') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    for (let i = 0; i < this.baseDistances.length; i++) arr[i] = this.baseDistances[i] + this.flowOffset;
    attr.needsUpdate = true;
  }
}
